import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useAttachmentPolicy } from "~/entities/chat";
import { runValidationAndUpload } from "~/features/upload-attachment/runValidationAndUpload";
import { useUploadAttachment } from "~/features/upload-attachment/useUploadAttachment";
import { useChatUIStore } from "~/shared/store/useChatUIStore";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import {
    buildFileInputAccept,
    computeAcceptedKinds,
    computeKindLimits,
    attachPoolView,
    countByKind,
    countUploadedImages,
    countUploadedVisualInputs,
    extractClipboardMediaFiles,
    isAllSlotsFull,
    itemsToRemoveOnKindChange,
    routePickedUploads,
    splitImageFiles,
} from "./attachment-helpers";
import { useImageRoleSlots } from "./useImageRoleSlots";
import { useReferenceChannel } from "./useReferenceChannel";

import type { ImageInputRole, ModeConfig } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

export interface UseChatAttachmentsParams {
    chatId: string | null;
    currentMode: ModeConfig | null;
}

/**
 * Куда кладёт выбранное открытый пикер: в конкретный роль-слот кадра или в
 * колонку референсов. Пусто — общий пул вложений (скрепка тулбара).
 */
export interface PickTarget {
    role?: ImageInputRole;
    channel?: "reference";
}

/**
 * Инкапсулирует attachment/upload concern промпт-бокса: лимиты и счётчики
 * по kind, политику типов, accept для file-input, picker-стейт, auto-cleanup
 * pending при смене режима и единый entry point загрузки (DnD/paste/file-picker
 * идут через `runValidationAndUpload`). Вся ветвистая логика — в чистых
 * хелперах `./attachment-helpers`; хук лишь оркеструет стор и эффекты.
 */
export function useChatAttachments({ chatId, currentMode }: UseChatAttachmentsParams) {
    const { t } = useTranslation("chat");
    const { items, upload, remove, reorder, reset } = useUploadAttachment(chatId);
    const addPending = useDraftChatStore((s) => s.addPending);
    const removePending = useDraftChatStore((s) => s.removePending);
    const setAttachPickerOpen = useChatUIStore((s) => s.setAttachPickerOpen);

    const [pickerOpen, setPickerOpen] = useState(false);
    const [generatedPickerOpen, setGeneratedPickerOpen] = useState(false);

    // Синхронизируем глобальный флаг attachPickerOpen со state любого
    // открытого picker'а — chat-wide DnD-зона блокирует drop пока юзер
    // выбирает из существующих, иначе одновременный drop файла и выбор
    // из picker'а — non-deterministic поведение.
    useEffect(() => {
        setAttachPickerOpen(pickerOpen || generatedPickerOpen);
    }, [pickerOpen, generatedPickerOpen, setAttachPickerOpen]);

    const policyQuery = useAttachmentPolicy();
    const attachPolicy = useMemo(() => {
        const p = policyQuery.data?.supportedTypes;
        if (!p) return null;
        return { image: p.image, video: p.video, audio: p.audio };
    }, [policyQuery.data]);

    // Референсы — свой канал со своим лимитом и своей очисткой; кадровый учёт
    // ниже идёт по `frameItems`, чтобы референс не занимал слот кадра и не
    // считался в «нужно ещё N изображений».
    const { maxReferences, referenceItems, frameItems, referenceRoom, uploadReference } = useReferenceChannel(
        { chatId, currentMode, items, policy: attachPolicy, upload, t },
    );

    const minImages = currentMode?.minInputImages ?? 0;
    const maxImages = currentMode?.maxInputImages ?? null;
    const uploadedImagesCount = countUploadedImages(frameItems);

    const kindLimits = useMemo(() => computeKindLimits(currentMode), [currentMode]);
    const kindCounts = useMemo(() => countByKind(frameItems), [frameItems]);

    // Кадрового канала у режима может не быть вовсе, но картинку он принимает —
    // каналом референсов. Скрепка и перетаскивание тогда работают как обычно,
    // меняется только адресат: иначе жест молча отбрасывался бы с «режим не
    // принимает изображения», хотя колонка «Референсы» рядом их ждёт.
    const imagesGoToReferences = maxImages === 0 && maxReferences > 0;
    const attachPool = useMemo(
        () =>
            attachPoolView({
                kindLimits,
                kindCounts,
                frameCount: frameItems.length,
                referenceCount: referenceItems.length,
                maxReferences,
                imagesGoToReferences,
            }),
        [
            kindLimits,
            kindCounts,
            frameItems.length,
            referenceItems.length,
            maxReferences,
            imagesGoToReferences,
        ],
    );

    const acceptedKinds = useMemo(() => computeAcceptedKinds(attachPool.limits), [attachPool.limits]);
    const supportsAnyAttachment = acceptedKinds.length > 0;
    const fileInputAccept = useMemo(
        () => buildFileInputAccept(acceptedKinds, attachPolicy),
        [acceptedKinds, attachPolicy],
    );
    const allSlotsFull = useMemo(
        () => isAllSlotsFull(attachPool.queued, acceptedKinds, attachPool.limits),
        [attachPool, acceptedKinds],
    );

    // Auto-cleanup pending при смене kind режима. Если юзер прикрепил video
    // для image-to-video, потом сменил mode на text-to-image (где video не
    // принимается) — убираем такие items, чтобы submit не обвалился
    // валидацией. items берём через ref, чтобы effect запускался только на
    // реальную смену acceptedKinds, а не на любое изменение pending.
    const acceptedKindsKey = acceptedKinds.join(",");
    const prevAcceptedKindsKeyRef = useRef(acceptedKindsKey);
    const itemsRef = useRef(items);
    itemsRef.current = items;
    useEffect(() => {
        const prev = prevAcceptedKindsKeyRef.current;
        prevAcceptedKindsKeyRef.current = acceptedKindsKey;
        if (!chatId || prev === acceptedKindsKey) return;
        const toRemove = itemsToRemoveOnKindChange(itemsRef.current, acceptedKindsKey);
        if (toRemove.length === 0) return;
        for (const item of toRemove) removePending(chatId, item.localId);
        toast(t("prompt.attachmentsRemoved", { count: toRemove.length }), { icon: "ℹ️" });
    }, [acceptedKindsKey, chatId, removePending, t]);

    const handlePickFiles = useCallback(
        async (files: File[]) => {
            if (!chatId) return;
            // Картинки уходят своим приёмником, когда кадров у режима нет: у канала
            // референсов собственный лимит и собственная пометка вложения.
            const { images, rest } = imagesGoToReferences
                ? splitImageFiles(files)
                : { images: [], rest: files };
            if (images.length > 0) await uploadReference(images);
            if (rest.length === 0) return;
            await runValidationAndUpload({
                files: rest,
                limits: kindLimits,
                currentCounts: kindCounts,
                policy: attachPolicy,
                upload,
                t,
            });
        },
        [chatId, imagesGoToReferences, uploadReference, kindLimits, kindCounts, attachPolicy, upload, t],
    );

    // Роль-слоты кадров — отдельный concern: раскладка pending по
    // ролям режима и загрузка в конкретный слот. «Достаточно ли файлов» в таком
    // режиме считается по ролям, а не по общему счётчику картинок.
    const uploadToRole = useCallback(
        (role: ImageInputRole, files: File[]) =>
            runValidationAndUpload({
                files,
                limits: kindLimits,
                currentCounts: kindCounts,
                policy: attachPolicy,
                upload: (accepted) => upload(accepted, { role }),
                t,
            }),
        [kindLimits, kindCounts, attachPolicy, upload, t],
    );

    const {
        imageRoles,
        hasImageRoles,
        itemByRole,
        requiredRoleCount,
        hasRequiredRoleFiles,
        handlePickFilesForRole,
    } = useImageRoleSlots({ chatId, currentMode, items: frameItems, minImages, uploadToRole, t });

    // Визуальный вход — картинка любого канала или видео. Требование считается
    // отдельно от кадрового: у мультимодального режима кадров нет вовсе, и
    // обязательный вход там набирается референсом или видео (одного аудио модели
    // мало). Режимы без такого требования (`minVisualInputs: 0`) не затрагиваются.
    const minVisualInputs = currentMode?.minVisualInputs ?? 0;
    const hasRequiredVisualInputs = countUploadedVisualInputs(items) >= minVisualInputs;

    const hasRequiredFiles =
        (hasImageRoles ? hasRequiredRoleFiles : uploadedImagesCount >= minImages) && hasRequiredVisualInputs;

    const handlePaste = useCallback(
        (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            // Фильтрация по принимаемым режимом kind'ам делается дальше через
            // validateAndSliceFiles. Если media-файлов нет — текст вставится дефолтом.
            const files = extractClipboardMediaFiles(event.clipboardData?.items);
            if (files.length === 0) return;
            event.preventDefault();
            void handlePickFiles(files);
        },
        [handlePickFiles],
    );

    // Куда положит выбранное открытый пикер. Диалог один на три точки входа
    // (роль-слот кадра, колонка референсов, скрепка тулбара), поэтому адресат
    // живёт рядом с `confirmPick`, а не россыпью в компоненте.
    const [pickTarget, setPickTarget] = useState<PickTarget | undefined>(undefined);

    const openPicker = useCallback((target: PickTarget | undefined, open: (value: boolean) => void) => {
        setPickTarget(target);
        open(true);
    }, []);

    // Лимиты того же диалога зависят от адресата: колонка референсов считает свой
    // пул, а не кадровый, иначе выбор набирался бы по кадровому лимиту и лишнее
    // молча отсекалось бы на подтверждении.
    const isReferencePick = pickTarget?.channel === "reference";
    const pickerLimits = isReferencePick ? { image: maxReferences, video: 0, audio: 0 } : attachPool.limits;
    const pickerCounts = isReferencePick
        ? { image: referenceItems.length, video: 0, audio: 0 }
        : attachPool.counts;

    // Общий confirm для обоих picker'ов: пустой выбор / отсутствие chatId
    // просто закрывают диалог, иначе items уходят в pending. Раскладку по каналам
    // считает чистое правило — его видно из тестов, у хука её нет.
    const confirmPick = useCallback(
        (picked: PendingUpload[], close: (open: boolean) => void, target?: PickTarget) => {
            const items = routePickedUploads(picked, { target, referenceRoom, imagesGoToReferences });
            if (chatId && items.length > 0) addPending(chatId, items);
            close(false);
        },
        [chatId, addPending, imagesGoToReferences, referenceRoom],
    );

    return {
        pickTarget,
        openPicker,
        pickerLimits,
        pickerCounts,
        items,
        frameItems,
        referenceItems,
        maxReferences,
        uploadReference,
        remove,
        reorder,
        clearAttachments: reset,
        handlePickFiles,
        handlePickFilesForRole,
        handlePaste,
        imageRoles,
        hasImageRoles,
        itemByRole,
        requiredRoleCount,
        pickerOpen,
        setPickerOpen,
        generatedPickerOpen,
        setGeneratedPickerOpen,
        confirmPick,
        kindLimits,
        kindCounts,
        fileInputAccept,
        supportsAnyAttachment,
        allSlotsFull,
        // Скрепка адресует общий приёмник, поэтому её лимит и счётчик — пуловые.
        // Кадровый ноль погасил бы пункт «Загрузить файл» у режима без кадров,
        // хотя картинку он принимает — каналом референсов рядом.
        maxImages: attachPool.limits.image,
        totalImagesInQueue: attachPool.counts.image,
        minImages,
        uploadedImagesCount,
        hasRequiredFiles,
        minVisualInputs,
        hasRequiredVisualInputs,
    };
}
